import { UserFriendlyError } from '@affine/error';
import {
  effect,
  Entity,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { isEqual } from 'lodash-es';
import { catchError, EMPTY, of, tap } from 'rxjs';

import { validateAndReduceImage } from '../../../utils/reduce-image';
import type { AccountProfile, AuthStore } from '../stores/auth';

export interface AuthSessionInfo {
  account: AuthAccountInfo;
  needsTermsAcceptance?: boolean;
}

export interface AuthAccountInfo {
  id: string;
  label: string;
  email?: string;
  info?: AccountProfile | null;
  avatar?: string | null;
}

export interface AuthSessionUnauthenticated {
  status: 'unauthenticated';
}

export interface AuthSessionAuthenticated {
  status: 'authenticated';
  session: AuthSessionInfo;
}

export type AuthSessionStatus = (
  | AuthSessionUnauthenticated
  | AuthSessionAuthenticated
)['status'];

export class AuthSession extends Entity {
  /** Account confirmed by a successful session response in this runtime. */
  readonly confirmedAccountId$ = new LiveData<string | null>(null);

  session$: LiveData<AuthSessionUnauthenticated | AuthSessionAuthenticated> =
    LiveData.from(this.store.watchCachedAuthSession(), null).map(session =>
      session
        ? {
            status: 'authenticated',
            session: session as AuthSessionInfo,
          }
        : {
            status: 'unauthenticated',
          }
    );

  status$ = this.session$.map(session => session.status);

  account$ = this.session$.map(session =>
    session.status === 'authenticated' ? session.session.account : null
  );

  hasRevalidated$ = new LiveData(false);

  // sl-2ulq.5: Terms acceptance gate — true when user needs to accept legal docs
  needsTermsAcceptance$ = this.session$.map(session =>
    session.status === 'authenticated'
      ? (session.session.needsTermsAcceptance ?? false)
      : false
  );

  waitForAuthenticated = (signal?: AbortSignal) =>
    this.session$.waitFor(
      session => session.status === 'authenticated',
      signal
    ) as Promise<AuthSessionAuthenticated>;

  isRevalidating$ = new LiveData(false);

  constructor(private readonly store: AuthStore) {
    super();
  }

  revalidate = effect(
    exhaustMapWithTrailing(() =>
      fromPromise(() => this.getSession()).pipe(
        catchError(error => this.recoverFromSessionRevalidationError(error)),
        tap(sessionInfo => {
          if (!isEqual(this.store.getCachedAuthSession(), sessionInfo)) {
            this.store.setCachedAuthSession(sessionInfo);
          }
        }),
        onStart(() => {
          this.isRevalidating$.next(true);
        }),
        onComplete(() => {
          this.isRevalidating$.next(false);
          this.hasRevalidated$.next(true);
        })
      )
    )
  );

  private async getSession(): Promise<AuthSessionInfo | null> {
    try {
      const session = await this.store.fetchSession();

      if (session?.user) {
        this.confirmedAccountId$.next(session.user.id);
        const account = {
          id: session.user.id,
          email: session.user.email,
          label: session.user.name,
          avatar: session.user.avatarUrl,
          info: session.user,
        };
        const result: AuthSessionInfo = {
          account,
          needsTermsAcceptance: session.needsTermsAcceptance,
        };
        return result;
      } else {
        this.confirmedAccountId$.next(null);
        return null;
      }
    } catch (e) {
      if (UserFriendlyError.fromAny(e).is('UNSUPPORTED_CLIENT_VERSION')) {
        this.confirmedAccountId$.next(null);
        return null;
      }
      throw e;
    }
  }

  private recoverFromSessionRevalidationError(error: unknown) {
    const friendly = UserFriendlyError.fromAny(error);

    if (friendly.isStatus(401)) {
      this.confirmedAccountId$.next(null);
      return of(null);
    }

    // Local-first bootstrap must not hang on transient backend failures.
    // Keep the cached auth snapshot and let focus/server-start events retry.
    return EMPTY;
  }

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler — without this,
    // waitFor resolves immediately before the fetch starts.
    this.isRevalidating$.next(true);
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  // sl-2ulq.5: Accept terms and revalidate session
  async acceptTerms() {
    await this.store.acceptTerms();
    await this.waitForRevalidation();
  }

  async removeAvatar() {
    await this.store.removeAvatar();
    await this.waitForRevalidation();
  }

  async uploadAvatar(file: File) {
    const reducedFile = await validateAndReduceImage(file);
    await this.store.uploadAvatar(reducedFile);
    await this.waitForRevalidation();
  }

  async updateLabel(label: string) {
    await this.store.updateLabel(label);
    await this.waitForRevalidation();
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
